import React, { useEffect, useMemo, useState } from "react";
import {
    X,
    Save,
    Loader2,
    Users,
    AtSign,
    PlusCircle,
    Search,
    UserCheck,
    UserPlus,
    Building2,
    Clock,
    FileText,
    FileSpreadsheet,
    Check,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    ShieldCheck,
} from "lucide-react";
import { splitEmails, uniq, safeJsonParseArray } from "./mailUtils";
import DarkAwareSelect from "../common/DarkAwareSelect";
import ToggleSwitch from "../common/ToggleSwitch";
import { mailApi } from "../../services/mailApi";
import { apiClient } from "../../utils/apiClient";
import { useAuth } from "../../contexts/AuthContext";

const SCHEDULE_TYPES = [
    { value: "Daily", label: "Günlük", desc: "Seçilen günlerde her gün" },
    { value: "Weekly", label: "Haftalık", desc: "Haftanın seçilen günü" },
    { value: "Monthly", label: "Aylık", desc: "Ayın seçilen günü" },
    { value: "Cron", label: "Gelişmiş (Cron)", desc: "Özel zamanlama ifadesi" },
];

const SCHEDULE_TYPE_INT_MAP = { Daily: 1, Weekly: 2, Monthly: 3, Cron: 4 };

function normalizeScheduleType(v) {
    if (v === null || v === undefined) return "Daily";
    if (typeof v === "number") {
        if (v === 0 || v === 1) return "Daily";
        if (v === 2) return "Weekly";
        if (v === 3) return "Monthly";
        if (v === 4) return "Cron";
        return "Daily";
    }
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return normalizeScheduleType(Number(s));
    if (s === "Daily" || s === "Weekly" || s === "Monthly" || s === "Cron") return s;
    return "Daily";
}

function normalizeReportFormat(v) {
    if (v === null || v === undefined) return "PDF";
    const s = String(v).trim();
    const sl = s.toLowerCase();
    if (s === "1" || sl === "pdf") return "PDF";
    if (s === "2" || sl === "excel" || sl === "xlsx") return "Excel";
    if (s === "3" || sl === "pdfandexcel" || sl === "both" || sl === "pdf+excel" || sl === "pdfexcel") return "Both";
    if (sl.includes("excel") && sl.includes("pdf")) return "Both";
    if (sl.includes("excel")) return "Excel";
    return "PDF";
}

function reportFormatToInt(v) {
    const nf = normalizeReportFormat(v);
    if (nf === "Excel") return 2;
    if (nf === "Both") return 3;
    return 1;
}

function scheduleTypeToInt(v) {
    const key = normalizeScheduleType(v);
    return SCHEDULE_TYPE_INT_MAP[key] ?? 1;
}

const FORMATS = [
    { value: "PDF", label: "PDF Dosyası", sub: "E-posta için ideal özet görünüm" },
    { value: "Excel", label: "Excel (.xlsx)", sub: "Detaylı filtreleme ve analiz" },
    { value: "Both", label: "PDF + Excel", sub: "Her iki format tek mailde gönderilir" },
];

const ALL_REPORTS = [
    { label: "Puantaj Raporu", value: "puantaj_raporu", permission: "reports:view_puantaj_raporu", category: "PDKS Raporları" },
    { label: "Aylık Yatay Puantaj / İmza Çizelgesi", value: "monthly_attendance_matrix", permission: "reports:view_monthly_attendance_matrix", category: "PDKS Raporları" },
    { label: "Günlük Devam Raporu", value: "daily_attendance", permission: "reports:view_daily_attendance", category: "PDKS Raporları" },
    { label: "Giriş-Çıkış Özet Raporu", value: "entry_exit_summary", permission: "reports:view_entry_exit_summary", category: "PDKS Raporları" },
    { label: "Ham Geçişler Raporu", value: "raw_transitions", permission: "reports:view_raw_transitions", category: "PDKS Raporları" },
    { label: "Geç Gelenler Raporu", value: "latecomers_report", permission: "reports:view_latecomers_report", category: "PDKS Raporları" },
    { label: "Erken Çıkanlar Raporu", value: "early_leavers_report", permission: "reports:view_early_leavers_report", category: "PDKS Raporları" },
    { label: "Devamsızlık (Gelmeyenler) Raporu", value: "absenteeism_report", permission: "reports:view_absenteeism_report", category: "PDKS Raporları" },
    { label: "Fazla Mesai Raporu", value: "overtime_report", permission: "reports:view_overtime_report", category: "PDKS Raporları" },
    { label: "İzin Kullanım Hareketleri Raporu", value: "leaves_report", permission: "reports:view_leaves_report", category: "PDKS Raporları" },
    { label: "Yetkisiz / Başarısız ve Konum Dışı Geçişler Raporu", value: "failed_pass_attempts", permission: "reports:view_failed_pass_attempts", category: "PDKS Raporları" },
    { label: "Sicil Listesi Raporu", value: "personnel_list", permission: "reports:view_personnel_list", category: "Sicil & İK Raporları" },
    { label: "Özlük Bilgileri Raporu", value: "personal_info", permission: "reports:view_personal_info", category: "Sicil & İK Raporları" },
    { label: "Yıllık İzin Bakiye Raporu", value: "leave_balance_report", permission: "reports:view_leave_balance_report", category: "Sicil & İK Raporları" },
    { label: "İşten Ayrılan Personeller Raporu", value: "terminated_employees", permission: "reports:view_terminated_employees", category: "Sicil & İK Raporları" },
    { label: "KVKK / Aydınlatma Onay Raporu", value: "legal_consent_report", permission: "reports:view_legal_consent_report", category: "Sicil & İK Raporları" },
];

const TARGET_SCOPES = [
    {
        value: "all",
        label: "Tüm Personeller",
        desc: "Firmadaki tüm aktif personeller tek raporda yer alır",
    },
    {
        value: "recipient_departments",
        label: "Sadece Alıcının Kendi Bölümleri (Yetki Kapsamına Göre)",
        desc: "Her alıcıya yalnızca kendi bölümünün veya yetkili olduğu bölümlerin (1 bölüm, 4 bölüm veya tümü) raporu ayrı gider",
    },
    {
        value: "departments",
        label: "Seçili Bölümler (Sabit Bölüm Bazlı)",
        desc: "Yandan seçtiğiniz bölümler tüm alıcılara ortak gönderilir",
    },
    {
        value: "own",
        label: "Sadece Kendi Bilgilerim",
        desc: "Yalnızca kendi sicil kaydınız raporda yer alır",
    },
];

const PERIOD_PRESETS = {
    Daily: [
        { value: "previous_day", label: "Dünün Verisi (Önerilen)" },
        { value: "today", label: "Bugünün Verisi" },
    ],
    Weekly: [
        { value: "previous_week", label: "Geçen Hafta (Son 7 Gün)" },
        { value: "this_week", label: "Bu Hafta (Pazartesi'den Bugüne)" },
    ],
    Monthly: [
        { value: "previous_month", label: "Geçen Ay (Tam Ay)" },
        { value: "this_month", label: "Bu Ay (Ay Başından Bugüne)" },
    ],
    Cron: [
        { value: "previous_day", label: "Önceki Gün (Dün)" },
        { value: "today", label: "Bugün" },
        { value: "previous_week", label: "Son 7 Gün" },
        { value: "previous_month", label: "Geçen Ay" },
    ],
};

const WEEKDAYS = [
    { value: 1, short: "Pzt", label: "Pazartesi" },
    { value: 2, short: "Sal", label: "Salı" },
    { value: 3, short: "Çar", label: "Çarşamba" },
    { value: 4, short: "Per", label: "Perşembe" },
    { value: 5, short: "Cum", label: "Cuma" },
    { value: 6, short: "Cmt", label: "Cumartesi" },
    { value: 0, short: "Paz", label: "Pazar" },
];

const inputCls =
    "border border-gray-200 dark:border-gray-700 rounded-xl w-full px-3.5 py-2.5 bg-gray-50/70 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all";

function safeJsonParseObject(text) {
    if (!text) return {};
    try {
        const obj = typeof text === "string" ? JSON.parse(text) : text;
        return obj && typeof obj === "object" && !Array.isArray(obj) ? obj : {};
    } catch {
        return {};
    }
}

function normId(v) {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
}

function toTimeParts(hhmm) {
    if (!hhmm) return { hour: 9, minute: 0 };
    const m = String(hhmm).match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return { hour: 9, minute: 0 };
    const hour = Math.min(23, Math.max(0, Number(m[1])));
    const minute = Math.min(59, Math.max(0, Number(m[2])));
    return { hour, minute };
}

function toHHMM(hour, minute) {
    const h = String(Math.min(23, Math.max(0, Number(hour) || 0))).padStart(2, "0");
    const m = String(Math.min(59, Math.max(0, Number(minute) || 0))).padStart(2, "0");
    return `${h}:${m}`;
}

function asArray(v) {
    return Array.isArray(v) ? v : [];
}

function normalizePersonnel(x) {
    const id = x?.id ?? x?.Id ?? x?.employeeId ?? x?.EmployeeId ?? x?.personnelId ?? x?.PersonnelId;
    const firstName = x?.firstName ?? x?.FirstName ?? x?.ad ?? x?.Ad ?? "";
    const lastName = x?.lastName ?? x?.LastName ?? x?.soyad ?? x?.Soyad ?? "";
    const fullName =
        x?.fullName ??
        x?.FullName ??
        x?.adSoyad ??
        x?.AdSoyad ??
        x?.name ??
        x?.Name ??
        `${firstName} ${lastName}`.trim();
    const email = String(x?.email ?? x?.Email ?? x?.mail ?? x?.Mail ?? "").trim().toLowerCase();
    const deptId = normId(x?.departmentId ?? x?.DepartmentId);
    const deptName = x?.departmentName ?? x?.DepartmentName ?? x?.bolumAdi ?? x?.BolumAdi ?? "";
    const posName = x?.positionName ?? x?.PositionName ?? "";
    const empNo = x?.employeeNumber ?? x?.EmployeeNumber ?? "";
    const scopeSummary = x?.scopeSummary ?? x?.ScopeSummary ?? (deptName ? `Kendi Bölümü (${deptName})` : "Kendi Bölümü");
    const scopeKind = x?.scopeKind ?? x?.ScopeKind ?? "single_dept";

    return {
        id: normId(id),
        fullName: String(fullName || "").trim(),
        email,
        deptId,
        deptName: String(deptName || "").trim(),
        posName: String(posName || "").trim(),
        empNo: String(empNo || "").trim(),
        scopeSummary: String(scopeSummary || "").trim(),
        scopeKind: String(scopeKind || "single_dept"),
    };
}

function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

/**
 * 10.000+ Personel Ölçeklenebilir, Sunucu Taraflı Arama & Sayfalamalı Akıllı Alıcı Seçici
 */
function SmartRecipientPicker({
    label,
    required = false,
    emails = [],
    onChangeEmails,
    departments = [],
    knownEmployeesMap,
    onRegisterKnownEmployees,
    showScopeBadges = false,
    disabled = false,
    placeholder = "Personel ara (ad, soyad, e-posta) veya harici mail yazıp Enter'a bas...",
}) {
    const [inputText, setInputText] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [deptFilter, setDeptFilter] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(8);

    const [browseOpen, setBrowseOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [pageItems, setPageItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [inputHintError, setInputHintError] = useState("");

    // Arama metni için 300ms debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(inputText.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [inputText]);

    // Kullanıcı arama yazdığında veya bölüm seçtiğinde rehber penceresini otomatik aç
    const shouldShowDirectory = browseOpen || debouncedQuery.length > 0 || Boolean(deptFilter);

    useEffect(() => {
        if (!shouldShowDirectory) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await mailApi.getDirectory({
                    search: debouncedQuery || undefined,
                    departmentId: deptFilter || undefined,
                    page,
                    pageSize,
                });
                if (cancelled) return;

                const emps = Array.isArray(res?.employees)
                    ? res.employees.map(normalizePersonnel).filter((p) => p.id && isEmail(p.email))
                    : [];

                setPageItems(emps);
                setTotalCount(Number(res?.totalCount ?? emps.length));
                setTotalPages(Math.max(1, Number(res?.totalPages ?? 1)));

                if (emps.length > 0) {
                    onRegisterKnownEmployees?.(emps);
                }
            } catch {
                if (!cancelled) {
                    setPageItems([]);
                    setTotalCount(0);
                    setTotalPages(1);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldShowDirectory, debouncedQuery, deptFilter, page, pageSize]);

    const normalizedSelectedSet = useMemo(() => {
        return new Set((emails || []).map((e) => String(e || "").trim().toLowerCase()).filter(Boolean));
    }, [emails]);

    function addEmails(rawList) {
        setInputHintError("");
        const candidates = rawList
            .flatMap((x) => splitEmails(x))
            .map((x) => x.trim().toLowerCase())
            .filter(Boolean);

        if (candidates.length === 0) return false;

        const invalid = candidates.filter((c) => !isEmail(c));
        if (invalid.length > 0) {
            setInputHintError(`Geçersiz e-posta formatı: ${invalid.join(", ")}`);
            return false;
        }

        const merged = uniq([...(emails || []).map((x) => x.toLowerCase()), ...candidates]);
        onChangeEmails(merged);
        return true;
    }

    function handleAddFromInput() {
        if (!inputText.trim()) return;
        const ok = addEmails([inputText]);
        if (ok) setInputText("");
    }

    function handleKeyDown(e) {
        if (e.key === "Enter" || e.key === "," || e.key === ";") {
            if (inputText.includes("@")) {
                e.preventDefault();
                handleAddFromInput();
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (pageItems.length === 1) {
                    togglePerson(pageItems[0]);
                    setInputText("");
                } else {
                    setBrowseOpen(true);
                }
            }
        }
    }

    function togglePerson(person) {
        if (disabled || !person?.email) return;
        onRegisterKnownEmployees?.([person]);
        const em = person.email.toLowerCase();
        if (normalizedSelectedSet.has(em)) {
            onChangeEmails((emails || []).filter((x) => x.toLowerCase() !== em));
        } else {
            onChangeEmails(uniq([...(emails || []).map((x) => x.toLowerCase()), em]));
        }
    }

    function removeEmail(targetEmail) {
        if (disabled) return;
        const t = String(targetEmail || "").toLowerCase();
        onChangeEmails((emails || []).filter((x) => x.toLowerCase() !== t));
    }

    function addCurrentPage() {
        if (disabled || pageItems.length === 0) return;
        onRegisterKnownEmployees?.(pageItems);
        const toAdd = pageItems.map((p) => p.email.toLowerCase()).filter(isEmail);
        onChangeEmails(uniq([...(emails || []).map((x) => x.toLowerCase()), ...toAdd]));
    }

    async function addAllMatchingInFilter() {
        if (disabled || totalCount === 0) return;
        setBulkLoading(true);
        try {
            const res = await mailApi.getDirectoryBulkEmails({
                search: debouncedQuery || undefined,
                departmentId: deptFilter || undefined,
            });
            const emps = Array.isArray(res?.employees)
                ? res.employees.map(normalizePersonnel).filter((p) => p.id && isEmail(p.email))
                : [];
            if (emps.length > 0) onRegisterKnownEmployees?.(emps);

            const bulkEmails = Array.isArray(res?.emails)
                ? res.emails.map((x) => String(x || "").trim().toLowerCase()).filter(isEmail)
                : emps.map((p) => p.email);

            onChangeEmails(uniq([...(emails || []).map((x) => x.toLowerCase()), ...bulkEmails]));
        } catch {
            setInputHintError("Toplu personel ekleme sırasında hata oluştu.");
        } finally {
            setBulkLoading(false);
        }
    }

    return (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-4 space-y-3">
            {/* Başlık + Seçili Sayısı */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {label} {required && <span className="text-red-500">*</span>}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        {emails.length} alıcı
                    </span>
                </div>

                {emails.length > 0 && !disabled && (
                    <button
                        type="button"
                        onClick={() => onChangeEmails([])}
                        className="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400 hover:underline"
                    >
                        Tümünü Temizle
                    </button>
                )}
            </div>

            {/* Seçili Alıcı Chip'leri */}
            <div className="min-h-[46px] max-h-40 overflow-y-auto p-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/70 border border-gray-200/80 dark:border-gray-700 flex flex-wrap items-center gap-1.5">
                {emails.length === 0 ? (
                    <span className="text-xs text-gray-400 dark:text-gray-500 px-1">
                        Henüz alıcı eklenmedi. Aşağıdaki arama kutusundan personel arayabilir, bölüm seçebilir veya dış e-posta adresi yazabilirsiniz.
                    </span>
                ) : (
                    emails.map((em) => {
                        const key = em.toLowerCase();
                        const person = knownEmployeesMap?.get(key);
                        return (
                            <span
                                key={key}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-medium border transition-all ${
                                    person
                                        ? "bg-blue-50/90 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800"
                                        : "bg-amber-50/90 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800"
                                }`}
                                title={
                                    person
                                        ? `Sicil Personeli: ${person.fullName} (${person.email}) — ${person.scopeSummary}`
                                        : `Harici E-Posta: ${em}`
                                }
                            >
                                {person ? (
                                    <UserCheck className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-none" />
                                ) : (
                                    <AtSign className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-none" />
                                )}
                                <span className="truncate max-w-[280px]">
                                    {person ? (
                                        <>
                                            <b className="font-semibold">{person.fullName}</b>{" "}
                                            <span className="opacity-75 font-mono text-[11px]">({person.email})</span>
                                        </>
                                    ) : (
                                        <span className="font-mono">{em}</span>
                                    )}
                                </span>
                                {showScopeBadges && person?.scopeSummary && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 text-[10px] font-semibold">
                                        {person.scopeSummary}
                                    </span>
                                )}
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => removeEmail(em)}
                                        className="p-0.5 rounded-md hover:bg-black/10 dark:hover:bg-white/10"
                                        title="Alıcıyı Kaldır"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        );
                    })
                )}
            </div>

            {/* Arama + Bölüm Seçimi + Rehber Aç/Kapat Barı */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        disabled={disabled}
                        value={inputText}
                        onChange={(e) => {
                            setInputText(e.target.value);
                            if (inputHintError) setInputHintError("");
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {inputText && (
                        <button
                            type="button"
                            onClick={() => setInputText("")}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {inputText.trim().includes("@") && (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={handleAddFromInput}
                        className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shadow-xs transition-all whitespace-nowrap"
                    >
                        <UserPlus className="w-3.5 h-3.5" />
                        Harici Ekle
                    </button>
                )}

                {departments.length > 0 && (
                    <select
                        value={deptFilter}
                        onChange={(e) => {
                            setDeptFilter(e.target.value);
                            setPage(1);
                            if (e.target.value) setBrowseOpen(true);
                        }}
                        disabled={disabled}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Bölüm Seç / Filtrele...</option>
                        {departments.map((d) => (
                            <option key={d.value} value={d.value}>
                                {d.label} {d.count !== undefined ? `(${d.count})` : ""}
                            </option>
                        ))}
                    </select>
                )}

                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setBrowseOpen((v) => !v)}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all whitespace-nowrap ${
                        shouldShowDirectory
                            ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                    <Users className="w-3.5 h-3.5" />
                    {shouldShowDirectory ? "Rehberi Gizle" : "Rehberden Seç (Sayfalı)"}
                </button>
            </div>

            {inputHintError && (
                <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{inputHintError}</span>
                </div>
            )}

            {/* Sadece arama yapıldığında, bölüm seçildiğinde veya "Rehberden Seç" tıklandığında açılan Sayfalı Personel Tablosu */}
            {shouldShowDirectory && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900/50">
                    {/* Rehber Üst Bar: Toplam Kayıt + Sayfadakileri / Bölümdekileri Toplu Ekle */}
                    <div className="bg-gray-50 dark:bg-gray-900 px-3.5 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <div className="font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-2">
                            <span>Sicil Rehberi</span>
                            <span className="px-2 py-0.5 rounded-md bg-gray-200/70 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[11px]">
                                Toplam {totalCount} personel
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            {pageItems.length > 0 && (
                                <button
                                    type="button"
                                    disabled={disabled || loading}
                                    onClick={addCurrentPage}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[11px] font-semibold"
                                >
                                    <PlusCircle className="w-3 h-3" />
                                    Bu Sayfadakileri Ekle ({pageItems.length})
                                </button>
                            )}

                            {(deptFilter || debouncedQuery) && totalCount > pageItems.length && (
                                <button
                                    type="button"
                                    disabled={disabled || bulkLoading}
                                    onClick={addAllMatchingInFilter}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[11px] font-semibold"
                                >
                                    {bulkLoading ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <PlusCircle className="w-3 h-3" />
                                    )}
                                    Filtredeki Tümünü Ekle ({totalCount})
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Sayfa İçeriği (Maksimum 8 satır) */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {loading ? (
                            <div className="p-5 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                Personeller getiriliyor...
                            </div>
                        ) : pageItems.length === 0 ? (
                            <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
                                {inputText.includes("@") ? (
                                    <span>
                                        Sicilde eşleşen personel bulunamadı.{" "}
                                        <button
                                            type="button"
                                            onClick={handleAddFromInput}
                                            className="font-semibold text-blue-600 dark:text-blue-400 underline"
                                        >
                                            "{inputText.trim()}" adresini harici e-posta olarak eklemek için tıklayın.
                                        </button>
                                    </span>
                                ) : (
                                    "Arama kriterine uygun e-postalı personel bulunamadı."
                                )}
                            </div>
                        ) : (
                            pageItems.map((p) => {
                                const isSelected = normalizedSelectedSet.has(p.email.toLowerCase());
                                return (
                                    <button
                                        key={`${p.id}-${p.email}`}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => togglePerson(p)}
                                        className={`w-full px-3.5 py-2 text-left flex items-center justify-between gap-2 text-xs transition-colors ${
                                            isSelected
                                                ? "bg-blue-50/70 dark:bg-blue-900/25 text-blue-900 dark:text-blue-200"
                                                : "hover:bg-gray-50 dark:hover:bg-gray-800/60 text-gray-700 dark:text-gray-200"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
                                            <div
                                                className={`w-4 h-4 rounded-md flex items-center justify-center border flex-none ${
                                                    isSelected
                                                        ? "bg-blue-600 border-blue-600 text-white"
                                                        : "border-gray-300 dark:border-gray-600"
                                                }`}
                                            >
                                                {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                                            </div>
                                            <span className="font-semibold truncate">{p.fullName}</span>
                                            {p.deptName && (
                                                <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px]">
                                                    {p.deptName}
                                                </span>
                                            )}
                                            {p.scopeSummary && (
                                                <span
                                                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                        p.scopeKind === "all"
                                                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                            : p.scopeKind === "multi_dept"
                                                            ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                                            : "bg-blue-50/70 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                                                    }`}
                                                >
                                                    {p.scopeSummary}
                                                </span>
                                            )}
                                        </div>
                                        <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                            {p.email}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Sayfalama (Pagination) Alt Bar */}
                    {totalPages > 1 && (
                        <div className="bg-gray-50 dark:bg-gray-900 px-3.5 py-2 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">
                                Sayfa <b>{page}</b> / <b>{totalPages}</b>
                            </span>

                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 disabled:opacity-40"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                    Önceki
                                </button>
                                <button
                                    type="button"
                                    disabled={page >= totalPages || loading}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 disabled:opacity-40"
                                >
                                    Sonraki
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function MailSubscriptionModal({
    open,
    onClose,
    onSave,
    saving,
    mode = "create",
    initial = null,
}) {
    const { permissions = [] } = useAuth();
    const [error, setError] = useState("");
    const [catalogReportItems, setCatalogReportItems] = useState(null);

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        apiClient("Report/catalog?includeCustom=true")
            .then((res) => {
                if (mounted && Array.isArray(res)) {
                    setCatalogReportItems(
                        res.map((r) => ({
                            label: r.title || r.reportType,
                            value: r.reportType,
                            permission: r.permissionKey,
                            category: r.category || "PDKS Raporları",
                        }))
                    );
                }
            })
            .catch(() => {
                if (mounted) setCatalogReportItems(null);
            });
        return () => {
            mounted = false;
        };
    }, [open]);

    const allowedReportOptions = useMemo(() => {
        if (Array.isArray(catalogReportItems)) {
            return catalogReportItems;
        }
        return ALL_REPORTS.filter((r) => !r.permission || permissions.includes(r.permission));
    }, [catalogReportItems, permissions]);

    const groupedReportOptions = useMemo(() => {
        const groups = new Map();
        for (const item of allowedReportOptions) {
            const cat = item.category || "Raporlar";
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat).push(item);
        }
        return Array.from(groups.entries());
    }, [allowedReportOptions]);

    // Bilinen personeller önbelleği (chip'lerde isim ve kapsam göstermek için)
    const [knownEmployeesMap, setKnownEmployeesMap] = useState(() => new Map());
    const [deptOptions, setDeptOptions] = useState([]);
    const [userScope, setUserScope] = useState("all");

    const [toEmails, setToEmails] = useState([]);
    const [ccEmails, setCcEmails] = useState([]);
    const [showCcSection, setShowCcSection] = useState(false);

    const [selectedDepts, setSelectedDepts] = useState([]);

    const [multiReportEnabled, setMultiReportEnabled] = useState(false);
    const [multiSelectedReports, setMultiSelectedReports] = useState([]);

    const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5, 6]);
    const [includeInlineSummary, setIncludeInlineSummary] = useState(true);

    const [form, setForm] = useState({
        id: null,
        isEnabled: true,
        name: "",
        scheduleType: "Daily",
        timeZone: "Europe/Istanbul",
        sendTime: "09:00",
        weeklyDow: 1,
        monthlyDom: 1,
        cron: "",
        reportType: "puantaj_raporu",
        reportFormat: "PDF",
        targetScope: "all",
        periodPreset: "previous_day",
        parametersJson: "{}",
    });

    function registerKnownEmployees(empList) {
        if (!Array.isArray(empList) || empList.length === 0) return;
        setKnownEmployeesMap((prev) => {
            const next = new Map(prev);
            for (const p of empList) {
                if (p?.email) next.set(p.email.toLowerCase(), p);
            }
            return next;
        });
    }

    // Modal açıldığında sadece bölüm listesini ve (edit modundaysa) seçili e-postaların personel karşılıklarını çöz
    async function loadInitialMetadata(emailsToResolve = []) {
        try {
            const resolveParam =
                emailsToResolve.length > 0 ? emailsToResolve.slice(0, 100).join(",") : undefined;
            const dir = await mailApi.getDirectory({
                page: 1,
                pageSize: 5,
                resolveEmails: resolveParam,
            });

            const depts = Array.isArray(dir?.departments)
                ? dir.departments.map((d) => ({
                      value: d.id ?? d.Id,
                      label: d.name ?? d.Name,
                      count: d.emailCount ?? d.EmailCount,
                  }))
                : [];
            setDeptOptions(depts);

            if (dir?.userScope) setUserScope(dir.userScope);

            const initialEmps = [
                ...(Array.isArray(dir?.employees) ? dir.employees : []),
                ...(Array.isArray(dir?.resolvedEmployees) ? dir.resolvedEmployees : []),
            ]
                .map(normalizePersonnel)
                .filter((p) => p.id && isEmail(p.email));

            registerKnownEmployees(initialEmps);
        } catch {
            // ignore
        }
    }

    useEffect(() => {
        if (!deptOptions.length || !selectedDepts.length) return;
        const map = new Map(deptOptions.map((o) => [o.value, o.label]));
        setSelectedDepts((prev) => prev.map((p) => ({ ...p, label: map.get(p.value) || p.label })));
    }, [deptOptions]);

    useEffect(() => {
        if (!open) return;
        setError("");
        setMultiReportEnabled(false);
        setMultiSelectedReports([]);

        if (mode === "edit" && initial) {
            const params = safeJsonParseObject(initial.parametersJson ?? initial.ParametersJson ?? "{}");

            const targetScope = params.targetScope ?? params.reportScope ?? "all";
            const periodPreset = params.periodPreset ?? params.period ?? "previous_day";

            const deptIdsRaw = params.departmentIds ?? params.departments ?? [];
            const deptIds = Array.isArray(deptIdsRaw) ? deptIdsRaw.map(normId).filter(Boolean) : [];

            const allowedWeekdays = asArray(params.allowedWeekdays)
                .map(normId)
                .filter((x) => x !== null && x >= 0 && x <= 6);
            setWeekdays(allowedWeekdays.length > 0 ? allowedWeekdays : [1, 2, 3, 4, 5, 6]);

            setIncludeInlineSummary(
                params.includeInlineSummary !== undefined ? !!params.includeInlineSummary : true
            );

            const toArr = safeJsonParseArray(initial.recipientsToJson ?? initial.RecipientsToJson)
                .concat(Array.isArray(initial.recipientsTo) ? initial.recipientsTo : [])
                .concat(Array.isArray(initial.RecipientsTo) ? initial.RecipientsTo : []);

            const ccArr = safeJsonParseArray(initial.recipientsCcJson ?? initial.RecipientsCcJson)
                .concat(Array.isArray(initial.recipientsCc) ? initial.recipientsCc : [])
                .concat(Array.isArray(initial.RecipientsCc) ? initial.RecipientsCc : []);

            const cleanTo = uniq(toArr.map((x) => String(x || "").trim().toLowerCase())).filter(isEmail);
            const cleanCc = uniq(ccArr.map((x) => String(x || "").trim().toLowerCase())).filter(isEmail);

            setToEmails(cleanTo);
            setCcEmails(cleanCc);
            setShowCcSection(cleanCc.length > 0);

            const sh = initial.sendHour ?? initial.SendHour ?? params.sendHour;
            const sm = initial.sendMinute ?? initial.SendMinute ?? params.sendMinute;
            const sendTime =
                sh !== undefined && sm !== undefined
                    ? toHHMM(sh, sm)
                    : params.sendTime ?? initial.sendTime ?? "09:00";

            const weeklyDow = normId(params.weeklyDow ?? initial.weeklyDow ?? initial.WeeklyDow) ?? 1;
            const monthlyDom = normId(params.monthlyDom ?? initial.monthlyDom ?? initial.MonthlyDom) ?? 1;

            setForm({
                id: normId(initial.id ?? initial.Id),
                isEnabled: !!(initial.isEnabled ?? initial.IsEnabled),
                name: initial.name ?? initial.Name ?? "",
                scheduleType: normalizeScheduleType(initial.scheduleType ?? initial.ScheduleType ?? "Daily"),
                timeZone: "Europe/Istanbul",
                sendTime,
                weeklyDow,
                monthlyDom,
                cron: initial.cron ?? initial.Cron ?? "",
                reportType: initial.reportType ?? initial.ReportType ?? "puantaj_raporu",
                reportFormat: normalizeReportFormat(initial.reportFormat ?? initial.ReportFormat ?? "PDF"),
                targetScope,
                periodPreset,
                parametersJson: initial.parametersJson ?? initial.ParametersJson ?? "{}",
            });

            if (deptIds.length > 0) {
                const map = new Map(deptOptions.map((o) => [o.value, o.label]));
                setSelectedDepts(
                    deptIds.map((id) => ({ value: id, label: map.get(id) || `Bölüm #${id}` }))
                );
            } else {
                setSelectedDepts([]);
            }

            loadInitialMetadata([...cleanTo, ...cleanCc]);
        } else {
            setToEmails([]);
            setCcEmails([]);
            setShowCcSection(false);
            setWeekdays([1, 2, 3, 4, 5, 6]);
            setIncludeInlineSummary(true);
            setSelectedDepts([]);

            setForm({
                id: null,
                isEnabled: true,
                name: "",
                scheduleType: "Daily",
                timeZone: "Europe/Istanbul",
                sendTime: "09:00",
                weeklyDow: 1,
                monthlyDom: 1,
                cron: "",
                reportType: allowedReportOptions[0]?.value || "puantaj_raporu",
                reportFormat: "PDF",
                targetScope:
                    userScope === "department"
                        ? "recipient_departments"
                        : userScope === "own"
                        ? "own"
                        : "all",
                periodPreset: "previous_day",
                parametersJson: "{}",
            });

            loadInitialMetadata([]);
        }
    }, [open, mode, initial]);

    useEffect(() => {
        if (!open || mode === "edit") return;
        if (
            allowedReportOptions.length > 0 &&
            !allowedReportOptions.some((r) => r.value === form.reportType)
        ) {
            setForm((p) => ({ ...p, reportType: allowedReportOptions[0].value }));
        }
    }, [open, mode, allowedReportOptions, form.reportType]);

    useEffect(() => {
        const opts = PERIOD_PRESETS[form.scheduleType] || [];
        if (opts.length === 0) return;
        if (!opts.some((o) => o.value === form.periodPreset)) {
            setForm((p) => ({ ...p, periodPreset: opts[0].value }));
        }
    }, [form.scheduleType]);

    const availableScopes = useMemo(() => {
        if (userScope === "own") return TARGET_SCOPES.filter((s) => s.value === "own");
        if (userScope === "department") return TARGET_SCOPES.filter((s) => s.value !== "all");
        return TARGET_SCOPES;
    }, [userScope]);

    const canSubmit = useMemo(() => {
        if (!form.name.trim()) return false;
        if (toEmails.length === 0) return false;

        if (mode === "create" && multiReportEnabled) {
            if ((multiSelectedReports?.length || 0) === 0) return false;
        } else {
            if (!form.reportType.trim()) return false;
        }

        if (!form.reportFormat) return false;
        if (form.targetScope === "departments" && (selectedDepts?.length || 0) === 0) return false;
        if (form.scheduleType === "Cron" && !form.cron.trim()) return false;
        if (form.scheduleType === "Daily" && (weekdays?.length || 0) === 0) return false;
        return true;
    }, [
        form,
        toEmails,
        selectedDepts,
        mode,
        multiReportEnabled,
        multiSelectedReports,
        weekdays,
    ]);

    function setField(k, v) {
        setForm((p) => ({ ...p, [k]: v }));
    }

    function toggleWeekday(v) {
        setWeekdays((prev) => {
            const set = new Set(prev || []);
            if (set.has(v)) set.delete(v);
            else set.add(v);
            return Array.from(set);
        });
    }

    function buildStructuredParametersJson(cleanTo, cleanCc) {
        const params = safeJsonParseObject(form.parametersJson);
        const tp = toTimeParts(form.sendTime);

        params.targetScope = form.targetScope;
        params.periodPreset = form.periodPreset;
        params.allowedWeekdays = uniq((weekdays || []).map((x) => Number(x))).filter((x) => x >= 0 && x <= 6);
        params.includeInlineSummary = !!includeInlineSummary;
        params.sendHour = tp.hour;
        params.sendMinute = tp.minute;
        params.weeklyDow = Number(form.weeklyDow);
        params.monthlyDom = Number(form.monthlyDom);

        if (form.targetScope === "departments") {
            params.departmentIds = (selectedDepts || []).map((d) => d.value);
        } else {
            delete params.departmentIds;
        }

        const toEmpIds = [];
        const extTo = [];
        for (const em of cleanTo) {
            const emp = knownEmployeesMap.get(em.toLowerCase());
            if (emp?.id) toEmpIds.push(emp.id);
            else extTo.push(em.toLowerCase());
        }

        const ccEmpIds = [];
        const extCc = [];
        for (const em of cleanCc) {
            const emp = knownEmployeesMap.get(em.toLowerCase());
            if (emp?.id) ccEmpIds.push(emp.id);
            else extCc.push(em.toLowerCase());
        }

        params.toEmployeeIds = uniq(toEmpIds);
        params.ccEmployeeIds = uniq(ccEmpIds);
        params.externalToEmails = uniq(extTo);
        params.externalCcEmails = uniq(extCc);

        return JSON.stringify(params);
    }

    function buildPayload() {
        const cleanTo = uniq(toEmails.map((x) => x.trim().toLowerCase())).filter(isEmail);
        const cleanCc = uniq(ccEmails.map((x) => x.trim().toLowerCase())).filter(isEmail);
        const tp = toTimeParts(form.sendTime);
        const mergedParamsJson = buildStructuredParametersJson(cleanTo, cleanCc);

        const payload = {
            id: form.id,
            isEnabled: !!form.isEnabled,
            name: form.name.trim(),
            recipientsTo: cleanTo,
            recipientsCc: cleanCc,
            recipientsToJson: JSON.stringify(cleanTo),
            recipientsCcJson: JSON.stringify(cleanCc),
            scheduleType: scheduleTypeToInt(form.scheduleType),
            timeZone: "Europe/Istanbul",
            sendHour: tp.hour,
            sendMinute: tp.minute,
            weeklyDow: Number(form.weeklyDow),
            weeklyDayOfWeek: Number(form.weeklyDow),
            monthlyDom: Number(form.monthlyDom),
            monthlyDayOfMonth: Number(form.monthlyDom),
            cron: form.cron || null,
            reportType: form.reportType.trim(),
            reportFormat: reportFormatToInt(form.reportFormat),
            parametersJson: mergedParamsJson,
        };

        payload.IsEnabled = payload.isEnabled;
        payload.Name = payload.name;
        payload.RecipientsToJson = payload.recipientsToJson;
        payload.RecipientsCcJson = payload.recipientsCcJson;
        payload.ScheduleType = payload.scheduleType;
        payload.TimeZone = payload.timeZone;
        payload.SendHour = payload.sendHour;
        payload.SendMinute = payload.sendMinute;
        payload.WeeklyDow = payload.weeklyDow;
        payload.WeeklyDayOfWeek = payload.weeklyDow;
        payload.MonthlyDom = payload.monthlyDom;
        payload.MonthlyDayOfMonth = payload.monthlyDom;
        payload.Cron = payload.cron;
        payload.ReportType = payload.reportType;
        payload.ReportFormat = payload.reportFormat;
        payload.ParametersJson = payload.parametersJson;

        if (mode === "create" && multiReportEnabled) {
            payload.__bulkCreate = true;
            payload.__bulkReports = (multiSelectedReports || []).map((r) => r.value);
        }

        return payload;
    }

    async function submit(e) {
        e?.preventDefault?.();
        if (!canSubmit || saving) return;
        setError("");
        try {
            await onSave?.(buildPayload());
        } catch (err) {
            setError(err?.message || "Kaydetme sırasında hata oluştu.");
        }
    }

    if (!open) return null;

    const title = mode === "edit" ? "Mail Aboneliğini Düzenle" : "Yeni Otomatik Rapor Aboneliği";
    const reportMultiOptions = allowedReportOptions.map((r) => ({ value: r.value, label: r.label }));

    return (
        <div className="fixed inset-0 z-50">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
                onClick={() => (!saving ? onClose?.() : null)}
            />

            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
                <div className="w-full max-w-5xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[92vh] overflow-hidden">
                    {/* Modal Header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-blue-300" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold">{title}</h2>
                                <p className="text-xs text-blue-200/80 mt-0.5">
                                    Rapor içeriğini, alıcıları ve otomatik gönderim takvimini belirleyin.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-xl">
                                <span className="text-xs font-medium text-blue-100">
                                    {form.isEnabled ? "Abonelik Aktif" : "Pasif"}
                                </span>
                                <ToggleSwitch
                                    checked={!!form.isEnabled}
                                    onChange={(v) => setField("isEnabled", v)}
                                    disabled={!!saving}
                                    label="Abonelik aktif"
                                />
                            </div>

                            <button
                                type="button"
                                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                                onClick={() => (!saving ? onClose?.() : null)}
                                title="Kapat"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Modal Body */}
                    <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
                        <div className="p-5 sm:p-6 space-y-6 overflow-y-auto flex-1 min-h-0 bg-gray-50/50 dark:bg-gray-950/40">
                            {error && (
                                <div className="p-4 text-sm rounded-2xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center gap-2.5">
                                    <AlertCircle className="w-5 h-5 flex-none" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* 1. KART: ABONELİK ADI & RAPOR SEÇİMİ */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-xs border border-gray-100 dark:border-gray-700/80 space-y-4">
                                <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                                        1. Abonelik & Rapor Tanımı
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                            Abonelik Adı <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            className={inputCls}
                                            value={form.name}
                                            onChange={(e) => setField("name", e.target.value)}
                                            placeholder="Örn: Günlük Puantaj – Bölüm Amirleri"
                                            required
                                        />
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                            E-posta konu başlığında ve abonelik listesinde bu isim görünür.
                                        </p>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Gönderilecek Rapor <span className="text-red-500">*</span>
                                            </label>
                                            {mode === "create" && (
                                                <button
                                                    type="button"
                                                    onClick={() => setMultiReportEnabled((v) => !v)}
                                                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-lg transition-colors ${
                                                        multiReportEnabled
                                                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                                            : "text-blue-600 dark:text-blue-400 hover:underline"
                                                    }`}
                                                >
                                                    <PlusCircle className="w-3.5 h-3.5" />
                                                    {multiReportEnabled ? "Tekli Rapor Seçimine Dön" : "Çoklu Rapor Seç"}
                                                </button>
                                            )}
                                        </div>

                                        {mode === "create" && multiReportEnabled ? (
                                            <>
                                                <DarkAwareSelect
                                                    isMulti
                                                    options={reportMultiOptions}
                                                    value={multiSelectedReports}
                                                    onChange={(vals) =>
                                                        setMultiSelectedReports(Array.isArray(vals) ? vals : [])
                                                    }
                                                    placeholder="Birden fazla rapor seçin..."
                                                    isDisabled={saving}
                                                />
                                                <p className="text-[11px] text-blue-600 dark:text-blue-400 mt-1">
                                                    Seçilen her rapor için aynı alıcı ve zamanlamayla ayrı abonelik oluşturulur.
                                                </p>
                                            </>
                                        ) : (
                                            <select
                                                className={inputCls}
                                                value={form.reportType}
                                                onChange={(e) => setField("reportType", e.target.value)}
                                            >
                                                {groupedReportOptions.length === 0 ? (
                                                    <option value="">Hesabınıza tanımlı rapor bulunmuyor</option>
                                                ) : (
                                                    groupedReportOptions.map(([cat, items]) => (
                                                        <optgroup key={cat} label={cat}>
                                                            {items.map((r) => (
                                                                <option key={r.value} value={r.value}>
                                                                    {r.label}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))
                                                )}
                                            </select>
                                        )}
                                    </div>
                                </div>

                                {/* Format + Rapor Dönemi */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-1">
                                    <div className="lg:col-span-2">
                                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                            Dosya Formatı <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                            {FORMATS.map((f) => {
                                                const active = form.reportFormat === f.value;
                                                return (
                                                    <button
                                                        key={f.value}
                                                        type="button"
                                                        disabled={saving}
                                                        onClick={() => setField("reportFormat", f.value)}
                                                        className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between ${
                                                            active
                                                                ? "border-blue-600 bg-blue-50/70 dark:bg-blue-900/30 dark:border-blue-500 ring-2 ring-blue-500/20"
                                                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-xs font-bold text-gray-900 dark:text-white">
                                                                {f.label}
                                                            </span>
                                                            {f.value === "Excel" ? (
                                                                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                                                            ) : (
                                                                <FileText className="w-4 h-4 text-rose-600" />
                                                            )}
                                                        </div>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                                            {f.sub}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                            Rapor Veri Dönemi
                                        </label>
                                        <select
                                            className={inputCls}
                                            value={form.periodPreset}
                                            onChange={(e) => setField("periodPreset", e.target.value)}
                                        >
                                            {(PERIOD_PRESETS[form.scheduleType] || PERIOD_PRESETS.Daily).map((p) => (
                                                <option key={p.value} value={p.value}>
                                                    {p.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                            Gönderim anında raporun hangi tarih aralığı için üretileceğini belirler.
                                        </p>
                                    </div>
                                </div>

                                {/* Rapor Kapsamı + Bölümler + Mail İçi Özet */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                            Raporda Yer Alacak Personel Kapsamı
                                        </label>
                                        <select
                                            className={inputCls}
                                            value={form.targetScope}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setField("targetScope", v);
                                                if (v !== "departments") setSelectedDepts([]);
                                            }}
                                        >
                                            {availableScopes.map((s) => (
                                                <option key={s.value} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                            {availableScopes.find((s) => s.value === form.targetScope)?.desc}
                                        </p>
                                    </div>

                                    {form.targetScope === "departments" ? (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                                Rapora Dahil Edilecek Bölümler <span className="text-red-500">*</span>
                                            </label>
                                            <DarkAwareSelect
                                                isMulti
                                                options={deptOptions}
                                                value={selectedDepts}
                                                onChange={(vals) =>
                                                    setSelectedDepts(Array.isArray(vals) ? vals : [])
                                                }
                                                placeholder="Bir veya daha fazla bölüm seçin..."
                                                isDisabled={saving}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200/70 dark:border-gray-700">
                                            <div>
                                                <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                                    E-Posta Gövdesinde Özet Tablo Göster
                                                </div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Dosya ekinin yanı sıra mail içinde ilk 15 kaydın özet tablosu yer alır.
                                                </div>
                                            </div>
                                            <ToggleSwitch
                                                checked={!!includeInlineSummary}
                                                onChange={setIncludeInlineSummary}
                                                disabled={saving}
                                                label="Özet Tablo"
                                            />
                                        </div>
                                    )}
                                </div>

                                {form.targetScope === "recipient_departments" && (
                                    <div className="p-3.5 rounded-xl bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/80 flex items-start gap-3 text-xs text-emerald-900 dark:text-emerald-200">
                                        <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-none mt-0.5" />
                                        <div>
                                            <div className="font-bold">
                                                Akıllı Yetki Bazlı Dağıtım (Kişiye Özel Bölüm Filtresi) Aktif
                                            </div>
                                            <p className="mt-0.5 text-emerald-800/90 dark:text-emerald-300/90 leading-relaxed">
                                                Bu abonelikte kaç farklı amir/yönetici eklerseniz ekleyin herkese tüm bölümler gitmez:
                                                Sistem her alıcının yetkisini kontrol eder; <b>tek bölümü olan amire sadece kendi bölümünü</b>,{" "}
                                                <b>birden fazla bölümden (örn. 4 bölüm) sorumlu yöneticiye sadece o 4 bölümü</b>,{" "}
                                                <b>tüm personelleri görmeye yetkili yöneticiye ise tüm bölümleri</b> otomatik ayırarak gönderir.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 2. KART: ALICILAR (SAYFALAMALI & ARAMALI AKILLI SEÇİCİ) */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-xs border border-gray-100 dark:border-gray-700/80 space-y-4">
                                <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                                            2. Alıcılar (Kime Gönderilecek?)
                                        </h3>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowCcSection((v) => !v)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        {showCcSection ? (
                                            <>
                                                <ChevronUp className="w-3.5 h-3.5" />
                                                Bilgi (CC) Alanını Gizle ({ccEmails.length})
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown className="w-3.5 h-3.5" />
                                                + Bilgi (CC) Alıcısı Ekle {ccEmails.length > 0 ? `(${ccEmails.length})` : ""}
                                            </>
                                        )}
                                    </button>
                                </div>

                                <SmartRecipientPicker
                                    label="Birincil Alıcılar (To)"
                                    required
                                    emails={toEmails}
                                    onChangeEmails={setToEmails}
                                    departments={deptOptions}
                                    knownEmployeesMap={knownEmployeesMap}
                                    onRegisterKnownEmployees={registerKnownEmployees}
                                    showScopeBadges={form.targetScope === "recipient_departments"}
                                    disabled={saving}
                                />

                                {showCcSection && (
                                    <SmartRecipientPicker
                                        label="Bilgi Alıcıları (CC - Opsiyonel)"
                                        emails={ccEmails}
                                        onChangeEmails={setCcEmails}
                                        departments={deptOptions}
                                        knownEmployeesMap={knownEmployeesMap}
                                        onRegisterKnownEmployees={registerKnownEmployees}
                                        showScopeBadges={false}
                                        disabled={saving}
                                        placeholder="CC için personel ara veya harici e-posta yazıp Enter'a bas..."
                                    />
                                )}
                            </div>

                            {/* 3. KART: ZAMANLAMA & GÖNDERİM GÜNLERİ */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-xs border border-gray-100 dark:border-gray-700/80 space-y-4">
                                <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                                        3. Zamanlama (Ne Zaman Gönderilecek?)
                                    </h3>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                    {SCHEDULE_TYPES.map((s) => {
                                        const active = form.scheduleType === s.value;
                                        return (
                                            <button
                                                key={s.value}
                                                type="button"
                                                disabled={saving}
                                                onClick={() => setField("scheduleType", s.value)}
                                                className={`p-3 rounded-xl border text-left transition-all ${
                                                    active
                                                        ? "border-blue-600 bg-blue-50/70 dark:bg-blue-900/30 dark:border-blue-500 ring-2 ring-blue-500/20"
                                                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                                }`}
                                            >
                                                <div className="text-xs font-bold text-gray-900 dark:text-white">
                                                    {s.label}
                                                </div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                    {s.desc}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {form.scheduleType !== "Cron" && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                                Gönderim Saati (TR)
                                            </label>
                                            <input
                                                type="time"
                                                className={inputCls}
                                                value={form.sendTime}
                                                onChange={(e) => setField("sendTime", e.target.value)}
                                            />
                                        </div>
                                    )}

                                    {form.scheduleType === "Weekly" && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                                Haftanın Günü
                                            </label>
                                            <select
                                                className={inputCls}
                                                value={form.weeklyDow}
                                                onChange={(e) => setField("weeklyDow", Number(e.target.value))}
                                            >
                                                {WEEKDAYS.map((d) => (
                                                    <option key={d.value} value={d.value}>
                                                        {d.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {form.scheduleType === "Monthly" && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                                Ayın Günü (1 - 31)
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={31}
                                                className={inputCls}
                                                value={form.monthlyDom}
                                                onChange={(e) =>
                                                    setField(
                                                        "monthlyDom",
                                                        Math.min(31, Math.max(1, Number(e.target.value) || 1))
                                                    )
                                                }
                                            />
                                            <p className="text-[11px] text-gray-500 mt-1">
                                                Kısa aylarda (örn. Şubat) otomatik olarak ayın son günü çalışır.
                                            </p>
                                        </div>
                                    )}

                                    {form.scheduleType === "Cron" && (
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                                Cron İfadesi (Dakika Saat Gün Ay HaftanınGünü) *
                                            </label>
                                            <input
                                                className={`${inputCls} font-mono`}
                                                placeholder="0 9 * * 1-5"
                                                value={form.cron}
                                                onChange={(e) => setField("cron", e.target.value)}
                                            />
                                            <p className="text-[11px] text-gray-500 mt-1">
                                                Örn: Hafta içi her sabah 09:00 için{" "}
                                                <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono">
                                                    0 9 * * 1-5
                                                </code>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {form.scheduleType === "Daily" && (
                                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-2">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Gönderim Yapılacak Günler
                                            </label>
                                            <div className="flex items-center gap-1.5 text-xs">
                                                <button
                                                    type="button"
                                                    onClick={() => setWeekdays([1, 2, 3, 4, 5])}
                                                    className="px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium"
                                                >
                                                    Hafta İçi (Pzt-Cum)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setWeekdays([1, 2, 3, 4, 5, 6])}
                                                    className="px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium"
                                                >
                                                    Pazar Hariç (Pzt-Cmt)
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setWeekdays([1, 2, 3, 4, 5, 6, 0])}
                                                    className="px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium"
                                                >
                                                    Her Gün
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-7 gap-2">
                                            {WEEKDAYS.map((d) => {
                                                const active = (weekdays || []).includes(d.value);
                                                return (
                                                    <button
                                                        key={d.value}
                                                        type="button"
                                                        disabled={saving}
                                                        onClick={() => toggleWeekday(d.value)}
                                                        className={`py-2 px-2 rounded-xl border text-xs font-semibold transition-all flex flex-col items-center justify-center gap-0.5 ${
                                                            active
                                                                ? "bg-blue-600 border-blue-600 text-white shadow-xs"
                                                                : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400"
                                                        }`}
                                                    >
                                                        <span>{d.short}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            Kapalı olan günlerde (örneğin Pazar tatilinde) otomatik mail gönderimi atlanır.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 bg-white dark:bg-gray-900">
                            <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                                Yapılan tüm ekleme ve değişiklikler <b>Güvenlik Denetim Logları</b> altına kaydedilir.
                            </div>

                            <div className="flex items-center gap-2.5 ml-auto">
                                <button
                                    type="button"
                                    className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Vazgeç
                                </button>

                                <button
                                    type="submit"
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all shadow-md ${
                                        canSubmit
                                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/20"
                                            : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                                    }`}
                                    disabled={!canSubmit || saving}
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    {mode === "edit" ? "Değişiklikleri Kaydet" : "Aboneliği Kaydet"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
